import { REPORT } from '@consts/reports';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, count } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { getUniqueDates } from '@utils/data';
import { chart } from '@utils/charts';
import { styleDate, styleDateRange } from '@utils/styles';

interface WarrantyRateByMonthProps {
    dateRange: { startDate: string; endDate: string };
}

export const WarrantyRateByMonth = ({ dateRange }: WarrantyRateByMonthProps) => {
    const [range, setRange] = useRange('WarrantyRateByMonthProduct');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.PRODUCTS_OVERALL_TOTAL_WARRANTY_RATE_BY_MONTH_BY_MATERIAL,
        params: { range, ...dateRange },
    });

    const dateKeys = getUniqueDates(data, range);
    const dates = dateKeys.map(({ date }) => date);
    const productNames = Array.from(new Set(data.map((item) => item.productName))).sort();
    const valueMap = data.reduce((acc, item) => {
        acc[`${item.productName}|${item.date}`] = item.warrantyPct;
        return acc;
    }, {} as Record<string, number>);

    const series = productNames.map((productName) => ({
        name: productName,
        data: dates.map((date) => valueMap[`${productName}|${date}`] ?? 0),
    }));

    return (
        <BaseReport
            setRange={setRange}
            title={'Warranty Rate by Month'}
            description={'This graph shows your overall warranty rate by month for each product.'}
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            range={range}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers: ['Date', 'Product Name', 'Warranty %'],
                data: data.map((item) => [item.date, item.productName, item.warrantyPct]),
                summaries: [count, count, average],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort],
            }}
            chartOptions={chart(300, [{ name: 'Warranty %', opposite: false }], dateKeys, series, undefined, {
                legend: {
                    align: 'right',
                    verticalAlign: 'top',
                },
            })}
        />
    );
};
